import React, { useState, useEffect } from 'react';

const SleekPaylinesVisualizer = () => {
  const [currentPayline, setCurrentPayline] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [autoPlay, setAutoPlay] = useState(true);
  
  const PAYLINES = [
    { id: 0, name: "top-row",    positions: [[0,0],[0,1],[0,2],[0,3],[0,4]], color: "from-red-500 to-pink-500" },
    { id: 1, name: "middle-row", positions: [[1,0],[1,1],[1,2],[1,3],[1,4]], color: "from-blue-500 to-indigo-500" },
    { id: 2, name: "bottom-row", positions: [[2,0],[2,1],[2,2],[2,3],[2,4]], color: "from-green-500 to-emerald-500" },
    { id: 3, name: "diag-TL→BR", positions: [[0,0],[1,1],[2,2],[1,3],[0,4]], color: "from-purple-500 to-violet-500" },
    { id: 4, name: "diag-BL→TR", positions: [[2,0],[1,1],[0,2],[1,3],[2,4]], color: "from-yellow-500 to-amber-500" },
    { id: 5, name: "V-down",     positions: [[0,0],[0,1],[1,2],[2,3],[2,4]], color: "from-cyan-500 to-blue-500" },
    { id: 6, name: "V-up",       positions: [[2,0],[2,1],[1,2],[0,3],[0,4]], color: "from-orange-500 to-red-500" },
    { id: 7, name: "zigzag-top", positions: [[0,0],[1,1],[0,2],[1,3],[0,4]], color: "from-emerald-500 to-green-500" },
    { id: 8, name: "zigzag-bottom", positions: [[2,0],[1,1],[2,2],[1,3],[2,4]], color: "from-pink-500 to-rose-500" },
    { id: 9, name: "zigzag-middle", positions: [[1,0],[0,1],[1,2],[2,3],[1,4]], color: "from-amber-500 to-yellow-500" },
  ];
  
  const SYMBOLS = [
    { name: "seven", icon: "7️⃣", bg: "bg-pink-600", border: "border-pink-400" },
    { name: "cherry", icon: "🍒", bg: "bg-pink-500", border: "border-pink-300" },
    { name: "lemon", icon: "🍋", bg: "bg-yellow-400", border: "border-yellow-300" },
    { name: "watermelon", icon: "🍉", bg: "bg-green-500", border: "border-green-400" },
    { name: "orange", icon: "🍊", bg: "bg-orange-400", border: "border-orange-300" },
    { name: "plum", icon: "🍇", bg: "bg-purple-500", border: "border-purple-300" },
    { name: "wild", icon: "🃏", bg: "bg-gradient-to-r from-violet-500 via-pink-500 to-violet-500", border: "border-violet-300" }
  ];
  
  // Auto cycle through paylines
  useEffect(() => {
    let timer;
    if (autoPlay) {
      timer = setInterval(() => {
        setIsAnimating(true);
        setTimeout(() => {
          setCurrentPayline((prev) => (prev + 1) % PAYLINES.length);
          setIsAnimating(false);
        }, 200);
      }, 2500);
    }
    
    return () => clearInterval(timer);
  }, [autoPlay]);
  
  const isPositionInPayline = (row, col) => {
    return PAYLINES[currentPayline].positions.some(
      ([r, c]) => r === row && c === col
    );
  };
  
  const getRandomSymbol = () => {
    return SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
  };
  
  // Generate grid with winning symbols on the payline
  const generateGrid = () => {
    const grid = Array(3).fill().map(() => Array(5).fill(null));
    
    // First, fill the winning payline with the same symbol
    const winningSymbol = getRandomSymbol();
    PAYLINES[currentPayline].positions.forEach(([row, col]) => {
      grid[row][col] = winningSymbol;
    });
    
    // Then fill remaining positions with random symbols
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 5; col++) {
        if (!grid[row][col]) {
          // Make sure we don't repeat the winning symbol too much
          let symbol;
          do {
            symbol = getRandomSymbol();
          } while (symbol.name === winningSymbol.name && Math.random() > 0.2);
          
          grid[row][col] = symbol;
        }
      }
    }
    
    return grid;
  };
  
  const [grid, setGrid] = useState(generateGrid());
  
  // Update grid when payline changes
  useEffect(() => {
    setGrid(generateGrid());
  }, [currentPayline]);
  
  return (
    <div className="flex flex-col bg-gray-900 text-white rounded-xl shadow-2xl">
      <div className="flex justify-between items-center mb-4 p-4">
        <div className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">
          Payline: <span className={`ml-2 text-transparent bg-clip-text bg-gradient-to-r ${PAYLINES[currentPayline].color}`}>
            {PAYLINES[currentPayline].name}
          </span>
        </div>
        
        <button 
          onClick={() => setAutoPlay(!autoPlay)}
          className={`bg-gray-800 px-3 py-1 rounded-md border ${autoPlay ? 'border-green-500 text-green-400' : 'border-gray-600 text-gray-400'}`}
        >
          {autoPlay ? 'Auto: ON' : 'Auto: OFF'}
        </button>
      </div>
      
      {/* Cabinet styling */}
      <div className="bg-gradient-to-b from-gray-800 via-gray-850 to-gray-900 rounded-xl border-4 border-gray-700 overflow-hidden shadow-lg mb-6 relative mx-4">
        {/* Cabinet decoration lights */}
        <div className="absolute top-0 left-0 w-full h-2 flex justify-around">
          {Array(7).fill().map((_, i) => (
            <div 
              key={`top-light-${i}`} 
              className={`w-2 h-2 rounded-full ${
                i % 3 === 0 ? "bg-red-500" : 
                i % 3 === 1 ? "bg-blue-500" : 
                "bg-yellow-500"
              } animate-pulse`}
              style={{ animationDelay: `${i * 0.2}s` }}
            ></div>
          ))}
        </div>
        
        <div className="p-4">
          {/* Payline name and weight display */}
          <div className="bg-gray-900/80 p-2 mb-4 rounded-lg border border-gray-700 text-center">
            <h2 className="text-lg font-semibold">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Win Weight:</span>
              <span className="ml-2 text-sm text-white font-mono">
                {PAYLINES[currentPayline].id < 3 ? '1.0x' : PAYLINES[currentPayline].id < 5 ? '0.7x' : PAYLINES[currentPayline].id < 7 ? '0.5x' : '0.4x'}
              </span>
              <span className="ml-2 text-xs text-gray-400">(Higher weight means more frequent wins on this payline)</span>
            </h2>
          </div>
          
          {/* Slot machine display */}
          <div className="bg-black p-4 rounded-lg relative overflow-hidden border border-gray-700">
            {/* Scan line effect */}
            <div className="absolute inset-0 pointer-events-none opacity-20" 
                 style={{ 
                   backgroundImage: 'linear-gradient(transparent 50%, rgba(0, 0, 0, 0.1) 50%)',
                   backgroundSize: '100% 4px',
                   animation: 'scanline 1s linear infinite'
                 }}>
            </div>
            
            <div className={`grid grid-cols-5 gap-2 transition-opacity duration-300 ${isAnimating ? 'opacity-50' : 'opacity-100'}`}>
              {Array(3).fill().map((_, rowIdx) => (
                Array(5).fill().map((_, colIdx) => {
                  const isPaylinesPosition = isPositionInPayline(rowIdx, colIdx);
                  const symbol = grid[rowIdx][colIdx];
                  
                  return (
                    <div 
                      key={`${rowIdx}-${colIdx}`}
                      className={`
                        w-full aspect-square flex items-center justify-center rounded-lg relative 
                        border-2 ${symbol.border} transition-all duration-300
                        ${isPaylinesPosition 
                          ? 'transform scale-105 z-10 shadow-lg shadow-pink-500/20' 
                          : 'opacity-70'}
                      `}
                    >
                      {/* Symbol background */}
                      <div className={`absolute inset-0 ${symbol.bg} ${isPaylinesPosition ? 'opacity-100' : 'opacity-80'} rounded-lg`}></div>
                      
                      {/* Symbol */}
                      <div className={`relative z-10 text-2xl ${isPaylinesPosition ? 'animate-bounce' : ''}`}>
                        {symbol.icon}
                      </div>
                      
                      {/* Win highlight for payline positions */}
                      {isPaylinesPosition && (
                        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 to-amber-500/20 rounded-lg z-5 animate-pulse"></div>
                      )}
                    </div>
                  );
                })
              ))}
            </div>
            
            {/* Payline overlay - visualize the line connecting positions */}
            <svg className="absolute top-0 left-0 w-full h-full pointer-events-none" style={{ zIndex: 20 }}>
              <defs>
                <linearGradient id={`gradient-${currentPayline}`} x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" style={{ stopColor: getStopColor(PAYLINES[currentPayline].color.split(' ')[0]) }} />
                  <stop offset="100%" style={{ stopColor: getStopColor(PAYLINES[currentPayline].color.split(' ')[1]) }} />
                </linearGradient>
              </defs>
              
              <path 
                d={PAYLINES[currentPayline].positions.map(([row, col], i) => {
                  // Calculate position in SVG coordinates
                  // Assuming each cell is approximately 20% of width and 33% of height
                  const x = (col * 20) + 10;
                  const y = (row * 33) + 16.5;
                  const percent = `${x}% ${y}%`;
                  return i === 0 ? `M ${percent}` : `L ${percent}`;
                }).join(' ')}
                stroke={`url(#gradient-${currentPayline})`}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="5,5"
                fill="none"
                className="animate-pulse"
              />
              
              {/* Add dots at connection points */}
              {PAYLINES[currentPayline].positions.map(([row, col], i) => {
                const x = (col * 20) + 10;
                const y = (row * 33) + 16.5;
                return (
                  <circle 
                    key={`point-${i}`} 
                    cx={`${x}%`} 
                    cy={`${y}%`} 
                    r="4" 
                    fill={`url(#gradient-${currentPayline})`} 
                    className="animate-pulse"
                  />
                );
              })}
            </svg>
          </div>
        </div>
      </div>
      
      {/* Payline selection - styled more like the casino buttons */}
      <div className="bg-gray-800/80 p-4 rounded-lg border border-gray-700 mx-4 mb-4">
        <h3 className="text-base mb-3 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 font-semibold">Select Payline Pattern</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {PAYLINES.map((payline, index) => (
            <button
              key={payline.id}
              onClick={() => {
                setAutoPlay(false);
                setCurrentPayline(index);
              }}
              className={`
                py-2 px-3 rounded-lg text-xs transition-all
                bg-gradient-to-r ${currentPayline === index 
                  ? payline.color + ' text-white shadow-lg transform scale-105 border border-white/20' 
                  : 'from-gray-700 to-gray-800 text-gray-300 hover:from-gray-600 hover:to-gray-700 border border-gray-700'}
              `}
            >
              {payline.name}
            </button>
          ))}
        </div>
      </div>
      
      {/* Info box at bottom */}
      <div className="bg-gradient-to-r from-blue-900/20 to-indigo-900/20 p-3 rounded-lg border border-blue-800/30 mx-4 mb-4 text-xs text-gray-300">
        <p className="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Match 3+ identical symbols on a payline to win. Different payline patterns have different win weights.
        </p>
      </div>
    </div>
  );
};

// Helper function to convert Tailwind color classes to actual colors
function getStopColor(colorClass) {
  const colorMap = {
    'from-red-500': '#ef4444',
    'to-pink-500': '#ec4899',
    'from-blue-500': '#3b82f6',
    'to-indigo-500': '#6366f1',
    'from-green-500': '#22c55e',
    'to-emerald-500': '#10b981',
    'from-purple-500': '#a855f7',
    'to-violet-500': '#8b5cf6',
    'from-yellow-500': '#eab308',
    'to-amber-500': '#f59e0b',
    'from-cyan-500': '#06b6d4',
    'to-blue-500': '#3b82f6',
    'from-orange-500': '#f97316',
    'to-red-500': '#ef4444',
    'from-emerald-500': '#10b981',
    'to-green-500': '#22c55e',
    'from-pink-500': '#ec4899',
    'to-rose-500': '#f43f5e',
    'from-amber-500': '#f59e0b',
    'to-yellow-500': '#eab308'
  };
  
  return colorMap[colorClass] || '#fff';
}

export default SleekPaylinesVisualizer;
